import { usePrevious } from '@/utils/use-previous-hook';
import { CountNumberUp } from '@/widgets/components/count-up';
import { renderIf } from 'deckard-ui/dist/components/RenderIf/RenderIf';
import styled from 'styled-components';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  white-space: nowrap;
  font-size: 16px;
  line-height: 1;
  font-weight: bold;
  color: #333;
`;

const Totals = styled.div`
  font-size: 14px;
  color: #7a8195;
  margin-left: 5px;
`;

export interface AnimatedCounterProps {
  count: number;
  countLabel: string;
}

/**
 * Function that applied animated affect for changing filtered numbers. Underlying solution
 * provided by CountUp.js.
 * Note: usePrevious is a take on useRef + useEffect. Basically, useRef has state without
 * causing rerenders and useEffect controls when to update the prev value.
 * This is important b/c countup will count up from 0 always without the prev number to
 * count up from.
 *
 * @param {number} props.count count
 * @param {string} props.countLabel label for the count
 */
function AnimatedCounter(props: AnimatedCounterProps) {
  const { count, countLabel } = props;
  const prevCount = usePrevious(count);

  if (!count && count !== 0) return null;

  return (
    <CountNumberUp
      label={countLabel}
      currentCount={count}
      prevCount={prevCount}
    />
  );
}

export const filteredItemLabels = {
  IDENTIFIED_PROPERTIES: 'identified properties',
  LICENSES: 'licenses',
  WAITLIST_APPLICATIONS: 'waitlist applications',
  LISTINGS: 'Listings',
  COMPLAINTS: 'complaints',
  COMPLAINT: 'complaint',
  OPEN_COMPLAINTS: 'open',
  DEFAULT: 'results',
};

export interface FilterCounterProps {
  // Primary count.
  count: number;
  // Label to use beside `count`.
  countLabel?: string;
  // optional count to display in brackets beside the primary count.
  secondaryCount: number;
  // Label to use beside the `secondaryCount`.
  secondaryCountLabel?: string;
}

function hasTotals(props: FilterCounterProps) {
  // Keep backwards-compatible behavior, as there may be callers with do not do type checking and passing strings
  // around. Can be prettified once all components have been migrated.
  return Boolean(props.secondaryCount);
}

// Displays totals conditionally, if the value is present.
const MaybeTotals = renderIf(hasTotals, Totals);

/**
 * Header with counters for filtered grids. Supports displaying a single item count, or an optional extra counter in
 * brackets
 */
export function FilterCounter(props: FilterCounterProps) {
  const {
    count,
    countLabel = filteredItemLabels.IDENTIFIED_PROPERTIES,
    secondaryCount,
    secondaryCountLabel = filteredItemLabels.LISTINGS,
  } = props;

  return (
    <Wrapper data-cy="filter-counter-text">
      <AnimatedCounter count={count} countLabel={countLabel} />
      <MaybeTotals {...props}>
        {`(${secondaryCount} ${secondaryCountLabel})`}
      </MaybeTotals>
    </Wrapper>
  );
}
